import { CommonModule } from '@angular/common';
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChildren,
} from '@angular/core';
import { RouterModule } from '@angular/router';

import {
  MatExpansionModule,
  MatExpansionPanel,
} from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';

import { LngLatBounds } from 'maplibre-gl';

import { NumeralPipe } from '@pipes/numeral.pipe';
import { SensingLocationViewModel } from '../alert-map/alert-map.component';

export type AlertCountModel = {
  alertMax: number;
  alertMin: number;
  prealertMax: number;
  prealertMin: number;
  alert: number;
  prealert: number;
};

export type AlertDetailModel = {
  deviceId?: string;
  deviceName?: string;
  status: string;
  value?: number;
  eventType: string;
  eventTrigger: string;
  threshold?: number;
  location: AlertLocationModel;
  eventAt?: string;
  reportedAt?: string;
};

export type AlertLocationModel = {
  longitude: number;
  latitude: number;
  eventAt: string;
  address?: string;
};

export type SensingHistoryModel = {
  deviceId: string;
  deviceName: string;
  alertDetails?: AlertDetailModel[];
  latestEventAt?: string;
};

export type EventDetectTypeSensor =
  | 'fallDetected'
  | 'mcdDetected'
  | 'lightExposure'
  | 'shockDetected';

@Component({
  selector: 'app-alert-detail',
  standalone: true,
  imports: [
    CommonModule,
    MatExpansionModule,
    MatIconModule,
    MatSnackBarModule,
    NumeralPipe,
    RouterModule,
  ],
  templateUrl: './alert-detail.component.html',
  styleUrl: './alert-detail.component.scss',
})
export class AlertDetailComponent implements OnChanges {
  @ViewChildren(MatExpansionPanel) panels = new QueryList<MatExpansionPanel>();

  /** アラート一覧 */
  @Input({ required: true }) public alertList: SensingHistoryModel[] = [];

  /** センサー値の単位 */
  @Input() public unit: string = '';

  /** マップ表示範囲 */
  @Input() public mapBounds: LngLatBounds | undefined;

  /** 選択されたマーカー情報 */
  @Input() public selectedMarker: SensingLocationViewModel | undefined;

  /** ローディング中か */
  @Input() public loading = false;

  /** 選択されたアラート詳細 */
  @Output() alertDetailChanged = new EventEmitter<AlertDetailModel>();

  /** 選択マーカーを変更した際に通知 */
  @Output() markerChanged = new EventEmitter<SensingLocationViewModel>();

  /** フィルターアラート一覧 */
  public filteredAlertList: SensingHistoryModel[] = [];

  /** 選択されたアラート詳細 */
  @Input() public selectedAlertDetail: AlertDetailModel | undefined;

  /** デバイス毎の各アラートの回数 */
  public alertCounts: { [key: string]: AlertCountModel } = {};

  /** 選択されたセンサー */
  @Input() public selectedSensingType: string = 'temperature';

  public eventDetectTypeSensorList: EventDetectTypeSensor[] = [
    'fallDetected',
    'lightExposure',
    'mcdDetected',
    'shockDetected',
  ];

  constructor(private snackBar: MatSnackBar) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['mapBounds'] !== undefined) {
      const mapBounds = changes['mapBounds'].currentValue;
      this.filteredAlertList = this.filterAlertsByBounds(
        this.alertList,
        mapBounds,
      );
      this.alertCounts = this.createAlertCounts(this.filteredAlertList);
    }
    if (changes['alertList'] !== undefined) {
      this.panels.forEach((panel) => {
        panel.close();
      });
      this.selectedAlertDetail = undefined;
    } else if (changes['selectedMarker'] !== undefined) {
      if (this.selectedMarker == undefined) {
        this.selectedAlertDetail = undefined;
      } else {
        const selectedMarker = changes['selectedMarker'].currentValue;
        this.filterAlertsByMarker(this.filteredAlertList, selectedMarker);
      }
    }
  }

  /**
   * 落下、動作、明暗、衝撃の検知系センサーかを判定する
   * @param selectedSensingType センサータイプ
   * @returns 落下、動作、明暗、衝撃の検知系センサーならTrue
   */
  public isSensor(selectedSensingType: string): boolean {
    return this.eventDetectTypeSensorList.includes(
      selectedSensingType as EventDetectTypeSensor,
    );
  }

  /**
   * ステータスのアラート判定をする
   * @param alertsPerDevice デバイス毎のアラート
   * @returns アラートならTrue
   */
  public isAlert(alertsPerDevice: SensingHistoryModel): boolean {
    const alertCount = this.alertCounts[alertsPerDevice.deviceId];
    return (
      alertCount &&
      (alertCount.alertMax >= 1 ||
        alertCount.alertMin >= 1 ||
        alertCount.alert >= 1)
    );
  }

  /**
   * イベント検知のアラートであるか
   * @param deviceId デバイスID
   * @returns Trueの場合、イベント検知のアラート
   */
  public isEventAlert(deviceId: string) {
    const counts = this.alertCounts[deviceId];
    return counts.alert > 0 || counts.prealert > 0;
  }

  /**
   * 選択したマーカー情報と一致した要素をクラスを付与する
   * @param alert アラート情報
   * @returns 一致の場合True
   */
  public isSelectedAlert(alert: AlertDetailModel) {
    return this.selectedAlertDetail === alert;
  }

  /**
   * アラート一覧をクリック時通知する
   * @param alert アラート情報
   * @param deviceName デバイス名
   * @param deviceId デバイスID
   */
  public selectedAlert(
    alert: AlertDetailModel,
    deviceName: string,
    deviceId: string,
  ) {
    if (alert.location?.latitude == null || alert.location?.longitude == null) {
      this.snackBar.open('位置情報がありません。', '閉じる', {
        duration: 5000,
      });
      return;
    }

    // 選択済みアラートを再度クリックしたときは選択済みアラートをundefinedとする。
    // それ以外の場合はクリックしたアラートを選択済みアラートとして保存する。
    if (this.selectedAlertDetail !== alert) {
      this.selectedAlertDetail = alert;
      this.alertDetailChanged.emit({ deviceName, deviceId, ...alert });
    } else {
      this.selectedAlertDetail = undefined;
      this.selectedMarker = undefined;
      this.alertDetailChanged.emit(undefined);
      this.markerChanged.emit(undefined);
    }
  }

  /**
   * マウスオーバー時にポインターと背景色を変更する
   * @param event MouseEvent
   */
  public onMouseEnter(event: MouseEvent) {
    const target = event.target as HTMLElement;
    target.style.cursor = 'pointer';
    target.style.backgroundColor = 'rgba(0, 0, 0, 0.04)';
  }

  /**
   * マウスを離れた際元のスタイルに変更する
   * @param event MouseEvent
   */
  public onMouseLeave(event: MouseEvent) {
    const target = event.target as HTMLElement;
    target.style.cursor = '';
    target.style.backgroundColor = '';
  }

  /**
   * 指定デバイスのアラート回数を取得する
   * @param deviceId デバイスID
   * @param key キー
   * @returns 回数
   */
  public deviceAlertCount(
    deviceId: string,
    key: keyof AlertCountModel,
  ): number {
    return this.alertCounts[deviceId][key];
  }

  /**
   * マーカー選択時にアラート情報一覧をフィルターする
   * @param alertList アラート情報
   * @param selectedMarker 選択マーカー
   */
  private filterAlertsByMarker(
    alertList: SensingHistoryModel[],
    selectedMarker: SensingLocationViewModel,
  ) {
    alertList.forEach((device) => {
      device.alertDetails?.forEach((alert) => {
        if (
          alert.location.latitude === selectedMarker.location.latitude &&
          alert.location.longitude === selectedMarker.location.longitude &&
          alert.eventAt === selectedMarker.eventAt
        ) {
          this.selectedAlertDetail = alert;
          this.updatePanels(device.deviceId, device.deviceName, alert.eventAt!);
        }
      });
    });
  }

  /**
   * パネルの状態を変更する
   * @param deviceId デバイスID
   * @param deviceName デバイス名
   * @param eventAt イベント発生日時
   */
  private updatePanels(deviceId: string, deviceName: string, eventAt: string) {
    this.panels.forEach((panel) => {
      const panelId = panel._body.nativeElement.parentElement?.id;

      if (panelId === deviceId || panelId === deviceName) {
        // パネルが既に開いているかを確認
        if (panel.expanded) {
          // パネルが既に開いている場合は直接スクロール
          document
            .getElementById(eventAt)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
        } else {
          // パネルが閉じている場合はafterExpandイベントでスクロール
          const scrollToElement = () => {
            document
              .getElementById(eventAt)
              ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
          };

          // パネルが完全に展開された後にイベントを登録
          const subscription = panel.afterExpand.subscribe(() => {
            scrollToElement();
            subscription.unsubscribe();
          });
          panel.open();
        }
      } else {
        panel.close();
      }
    });
  }

  /**
   * 指定された範囲内のアラートをフィルタリングし集計する
   * @param alertList アラートの一覧
   * @param bounds マップ表示範囲
   * @returns フィルタリングされたアラートの一覧
   */
  private filterAlertsByBounds(
    alertList: SensingHistoryModel[],
    bounds: LngLatBounds,
  ): SensingHistoryModel[] {
    const filteredList: SensingHistoryModel[] = [];

    alertList.forEach((device) => {
      // マップ範囲に存在するアラート詳細を取得
      const filteredDetails = device.alertDetails?.filter((alert) => {
        // 位置情報がないアラートの場合、マップ範囲に関係なく常に表示する
        // 位置情報がある場合、マップ範囲にあるものに絞る
        const noAlertLocation =
          alert.location.longitude == null || alert.location.latitude == null;
        const isWithinBounds =
          alert.location.longitude >= bounds._sw.lng &&
          alert.location.longitude <= bounds._ne.lng &&
          alert.location.latitude >= bounds._sw.lat &&
          alert.location.latitude <= bounds._ne.lat;
        return noAlertLocation || isWithinBounds;
      });

      if (filteredDetails && filteredDetails.length > 0) {
        filteredList.push({
          ...device,
          alertDetails: filteredDetails,
        });
      }
    });

    filteredList.sort(this.compareAlert);

    return filteredList;
  }

  private createAlertCounts(deviceAlertList: SensingHistoryModel[]) {
    const alertCounts: { [key: string]: AlertCountModel } = {};

    for (const deviceAlert of deviceAlertList) {
      // アラートごとの数
      const newAlertCounts: AlertCountModel = {
        alertMax: 0,
        alertMin: 0,
        prealertMax: 0,
        prealertMin: 0,
        alert: 0,
        prealert: 0,
      };

      deviceAlert.alertDetails?.forEach((alert) => {
        this.updateCountSummary(newAlertCounts, alert.status);
      });

      alertCounts[deviceAlert.deviceId] = newAlertCounts;
    }

    return alertCounts;
  }

  private compareAlert(a: SensingHistoryModel, b: SensingHistoryModel): number {
    const aName = a.deviceName || a.deviceId;
    const bName = b.deviceName || b.deviceId;
    if (aName === bName) {
      return 0;
    } else {
      return aName > bName ? 1 : -1;
    }
  }

  /**
   * アラートのステータスに基づいてカウントサマリーを更新する
   * @param countSummary 集計モデル
   * @param status アラートのステータス
   */
  private updateCountSummary(
    countSummary: AlertCountModel,
    status: string,
  ): void {
    switch (status) {
      case 'AlertMax':
        countSummary.alertMax += 1;
        break;
      case 'AlertMin':
        countSummary.alertMin += 1;
        break;
      case 'PreAlertMax':
        countSummary.prealertMax += 1;
        break;
      case 'PreAlertMin':
        countSummary.prealertMin += 1;
        break;
      case 'Alert':
        countSummary.alert += 1;
        break;
      case 'PreAlert':
        countSummary.prealert += 1;
        break;
    }
  }
}
